import { Coins } from './app-impact-coins';
import { Face } from './app-impact-face';
import { useRef } from 'react';
import { ArtColoursProvider, useArtColours, type ArtColourProps } from '../motion/art-colours';
import { Animate, AnimateTransform, SmilTimeline, usePauseOutOfView, type Key, type Keys } from '../motion/smil';
import { CARD, LOOP, useLine } from './app-impact-style';

// MARK: Hand

/** Offsets of the tapping hand. The screen faces her, so at 1.58s the finger taps behind the phone. */
const HAND: readonly (readonly [seconds: number, dx: number, dy: number, ease?: Key[2]])[] = [
	[1, 0, 0],
	[1.18, 8, 4],
	[1.45, -36, 6],
	[1.58, -46, 12, 'in'],
	[1.72, -42, 9, 'out'],
	[3.6, -42, 9],
	[4.1, 0, 0],
];

function handKeys(value: (dx: number, dy: number) => string): Keys {
	const [first, ...rest] = HAND.map(([t, dx, dy, ease]): Key => [t, value(dx, dy), ease]);
	return first ? [first, ...rest] : [[0, value(0, 0)]];
}

/** The sleeve stays pinned at the elbow and the hem while the cuff follows the hand. */
function forearm(dx: number, dy: number) {
	const at = (x: number, y: number, k = 1) => `${(x + dx * k).toFixed(2)} ${(y + dy * k).toFixed(2)}`;
	return [
		'M745.301 1069.41C745.301 1069.41',
		at(618.711, 980.601, 0.8),
		at(585.821, 963.391),
		`C${at(583.141, 961.991)}`,
		at(579.841, 962.731),
		at(578.031, 965.151),
		`L${at(516.081, 1048.09)}`,
		`C${at(514.511, 1050.2)}`,
		at(514.441, 1053.06),
		at(515.921, 1055.23),
		`C${at(534.291, 1082.17)}`,
		at(654.361, 1245.82, 0.3),
		'802.741 1153.71',
	].join(' ');
}

/**
 * Same command list in both shapes, so SMIL can morph the curled finger into a pointing one. The pointing
 * finger's underside ends on the middle finger's outline (t = 0.45 of its curve), where it passes under.
 */
const INDEX_CURLED =
	'M574.52 970.092C574.52 970.092 564.9 900.362 485.57 911.572C485.57 911.572 446.65 918.962 466.43 945.822C466.43 945.822 466.43 945.822 466.43 945.822C466.43 945.822 466.43 945.822 466.43 945.822';
const INDEX_POINTING =
	'M574.52 970.092C574.52 970.092 564.9 900.362 485.57 911.572C470 913.5 445 912 428 913C416 914 415 933 428 934C444 934.6 462 936.4 477.86 939.66';

const MIDDLE_FINGER = 'M504.22 940.17C504.22 940.17 437.56 930.27 464.78 981.22';

function Hand() {
	const line = useLine();
	const paper = useArtColours().paper;
	return (
		<>
			<path d={forearm(0, 0)} {...line}>
				<Animate attr="d" keys={handKeys(forearm)} />
			</path>
			<g>
				<AnimateTransform type="translate" keys={handKeys((dx, dy) => `${dx} ${dy}`)} />
				<path d={INDEX_CURLED} {...line}>
					<Animate
						attr="d"
						keys={[
							[1.1, INDEX_CURLED],
							[1.42, INDEX_POINTING],
							[3.65, INDEX_POINTING],
							[3.9, INDEX_CURLED],
						]}
					/>
				</path>
				{/* Only while the index points: filled, the middle finger hides it as it curls back underneath. */}
				<path d={MIDDLE_FINGER} fill={paper} opacity={0}>
					<Animate
						attr="opacity"
						keys={[
							[1.1, 0],
							[1.12, 1],
							[4, 1],
							[4.02, 0],
						]}
					/>
				</path>
				<path d={MIDDLE_FINGER} {...line} />
				<path
					d="M506.56 968.59C506.56 968.59 448 972.05 463.87 997.62C472.16 1010.98 498.46 997.62 498.46 997.62"
					{...line}
				/>
				<path d="M520.6 1041.13C520.6 1041.13 481.36 1038.02 484.51 1002.83" {...line} />
			</g>
		</>
	);
}

// MARK: Phone

function Phone() {
	const line = useLine();
	const paper = useArtColours().paper;
	return (
		<>
			<g>
				<AnimateTransform
					type="translate"
					keys={[
						[1.56, '0 0'],
						[1.62, '0 5', 'out'],
						[1.82, '0 0'],
					]}
				/>
				<path
					d="M282.341 1008.81L275.851 1060.14C274.191 1073.28 284.431 1084.9 297.681 1084.9H379.071C390.311 1084.9 399.741 1076.43 400.951 1065.25L418.961 897.899C420.361 884.889 410.171 873.539 397.081 873.539H325.961C315.271 873.539 306.131 881.219 304.291 891.749L303.001 905.109"
					{...line}
					fill={paper}
				/>
				<path
					d="M257.3 939.488C244.29 929.438 247.15 908.578 281.29 906.058C315.43 903.538 330.59 898.378 332.05 913.438C334.83 942.018 270.3 949.538 257.29 939.488H257.3Z"
					{...line}
				/>
				<path
					d="M287.9 978.941C287.9 978.941 269.35 984.381 257.64 975.331C245.93 966.281 248.5 947.511 279.24 945.231C309.98 942.951 321.46 938.701 324.94 951.881C328.23 964.331 318.62 973.541 287.9 978.941Z"
					{...line}
				/>
				<path
					d="M288.07 1009.02C288.07 1009.02 277.42 1013.68 270.7 1005.93C263.98 998.191 265.45 982.121 283.1 980.181C300.75 978.241 306.88 979.951 309.34 985.871C313.49 995.861 305.71 1004.41 288.08 1009.02H288.07Z"
					{...line}
				/>
			</g>
			<g opacity={0}>
				<Animate
					attr="opacity"
					keys={[
						[1.66, 0],
						[1.74, 1],
						[2.05, 0],
					]}
				/>
				<AnimateTransform
					type="translate"
					keys={[
						[1.66, '0 6'],
						[2.05, '0 -8', 'out'],
					]}
				/>
				<path d="M322 856L312 842M350 850V834M378 856L388 842" {...line} />
			</g>
		</>
	);
}

/**
 * She taps her phone, the floating coins fly up and out of the card, and new ones rise from the
 * screen to take their places. Its static twin is `MobileDonorIllustration` (`../static/mobile-donor.tsx`).
 * `paper` must match what sits behind it: the phone and a curling finger fill with it to hide lines.
 * `bare` has no card to clip a leaving coin, so the artboard's edge does.
 */
export function AppImpactIllustration(props: ArtColourProps & { label: string; className?: string; bare?: boolean }) {
	return (
		<ArtColoursProvider colours={props} paper={CARD}>
			<AppImpactSvg label={props.label} className={props.className} bare={props.bare} />
		</ArtColoursProvider>
	);
}

function AppImpactSvg(props: { label: string; className?: string; bare?: boolean }) {
	const line = useLine();
	const ref = useRef<SVGSVGElement>(null);
	usePauseOutOfView(ref);
	return (
		<SmilTimeline seconds={LOOP}>
			{/* `overflow`: a leaving coin must be clipped by the card's rounded edge, not the artboard. */}
			<svg
				ref={ref}
				viewBox="119 397 760 787"
				fill="none"
				role="img"
				aria-label={props.label}
				overflow={props.bare ? undefined : 'visible'}
				className={props.className}
			>
				<Face />
				<path d="M530.591 770.77C530.591 770.77 444.631 780.52 385.461 871.58" {...line} />
				<path d="M802.741 1153.71C964.111 1053.54 839.841 748.581 637.901 755.321" {...line} />
				<Hand />
				<Phone />
				<Coins />
			</svg>
		</SmilTimeline>
	);
}
